// The analytics event dictionary: every event the site sends, what it means and its props.
// Names are "<area>.<action>". Values that vary a lot (prices, sizes) are sent as buckets so the
// dashboard can group them. Keep this list in sync with what the code sends and with the breakdowns
// in the backend's analytics.py.
//
// Session / views (sent by tracker.js)
//   session.start      referrer (domain | direct | internal), utm_source/medium/campaign, landing_view, viewport, returning
//   session.end        active_s (visible time so far this session)
//   view.show          view (kartvy | listvy)
//   view.switch        from, to
//   view.time          view, active_s (time spent in that view)
//
// Search
//   search.select      type (kommun | ort | lan | postort | omrade | omrade-word | place), label, kommun,
//                      regso, position (1-based in the suggestion list)
//   search.no_match    q (what was typed, lowercase, max 60 chars)
//   search.remove      type, label
//   search.merged      removed (count of smaller searches replaced by a bigger one)
//   search.near_me     result (found | denied | outside | unsupported | error), kommun (Nära mig: listvy, landing, kartvy)
//
// Map
//   map.area_toggle    selected (bool), regso, within_search (listvy: deselect inside a bigger search), search (kartvy: added to / taken off the areas picked in the search)
//   map.area_box       selected (bool: the box turned its areas on), count (areas that changed); shift + drag a box over areas
//   map.area_popup     regso, open (bool), view (kartvy | listvy), open_count (areas compared after it), input (mouse |
//                      touch, on open), how (double_click | close_button: its ✕ in the card | map_number: its number
//                      on the map | close_all: the card's ✕, on close), seconds (bucket, on close) — an area added to or taken out of the comparison card
//                      (analytics/areaCards.js; named after the sticky cards it replaced)
//   map.area_compare   view, peak (most areas at once: 1 | 2 | 3 | 4 | 5+), opened (areas added, same buckets),
//                      seconds (<10s | 10-30s | 30s-2m | 2-5m | 5m+), ended (closed | page_hide | left_view)
//                      — one "comparison stretch": from the first area added until none is left
//   map.metric         metric (totalScore | income | …), enabled
//   map.colour_mode    mode (colours | lines | off; none until 2026-11-01) — the Färger toggle's new state
//   map.listings_toggle hidden (bool)
//   map.recenter       — picked (bool: fitted the picked areas rather than the country)
//   map.close_popups   —
//   map.list_locate    action (show), view (listvy: a feed card's; none: kartvy's rail card) + listingProps — a card's
//                      "Visa på kartan"
//   map.list_area_fold folded (bool) — an area header in kartvy's rail folded or unfolded its homes
//   map.transit_stop   kind (bus | subway), lines (count found), input (mouse | touch) — a bus stop's or tunnelbana
//                      station's card was opened (transitStops.js)
//
// Sharing an area and the area page (/omrade/<code>, src/area/AreaPage.js)
//   area.share         method (copy | native), source (card: the area card's Dela | page: the area page's |
//                      compare_card: the comparison card's Dela with one area), regso
//   area_page.view     regso, score, place (bool: a shared place's link) — an area page was shown (a shared
//                      link or a neighbour on its map)
//   area_page.action   action (listings | listings_all | map | listing | neighbour | place_map), regso (+ listing_id
//                      for listing, to for neighbour)
//
// Comparing areas (the comparison card, CompareCard.js; the page /jamfor/<codes>, src/area/ComparePage.js)
//   compare.share      method (copy | native), source (card | page | preview; map: the old comparison bar), count
//                      — a comparison's link was shared
//   compare_page.view  count — a shared comparison was opened
//   compare.preview    count, view (kartvy | listvy) — the card's "Jämför i storvy" opened the page over the view (its
//                      compare_page.action events then carry preview: true)
//   compare.reorder    count, source (card | page | preview), view (kartvy | listvy; card only) — a column was
//                      dragged by its area's name to another place
//   compare_page.action action (listings | map | listings_cta | map_cta | listings_one | area | listing |
//                      neighbour), count (+ regso for one area's, listing_id for listing, to for neighbour)
//
// The "Gå till" pin and its place card in kartvy (placePin.js, PlaceCard.js)
//   place.pin          action (drop | open | remove | show_homes), kind (address | street | place | area | multi),
//                      from (search | link | pick | drag; drop), reason (search | clear | close_all | far: the map
//                      panned far from it | drag: a new pin taken out of the menu; remove; x until 2026-11-01)
//   place.pick_mode    on (true | false) — the pill menu's Adress tool was turned on or off
//   place.pick         result (address | none | error), via (click: the Adress tool on | drag: its pin dropped on the
//                      map, desktop)
//   place.share        method (copy | native), source (place_card | page), regso — a place's link was shared
//
// Filters
//   filter.apply       fields (changed from default), type, price_min/max, rooms_min/max, area_min/max, fee_max, sqm_price_min/max, floor_min/max, year_built_min/max (fields also lists balcony, elevator)
//                      (bucketed), score_metrics (area-score filters in use), score_<metric> ("min-max")
//   filter.detailed    on (bool) — the area filter's Detaljerat switch (detailed scores instead of the total)
//
// Listings  (listing.open/realtor_click/favorite/share carry listing_id, the listing's key "bostad/<n>" /
//            "annons/<n>" from listingProps, so the admin can count opens/favourites/click-throughs per listing)
//   listing.open       source (map | feed | map_marker | share_link), type, price (bucket), kommun, regso, score, listing_id
//   listing.photos     count (photos viewed), set (images | floorplans)
//   listing.gallery    from (collage | all | floorplan), photos — the full-screen photo gallery was opened
//   listing.realtor_click  type, price (bucket), kommun, regso — the outbound conversion
//   listing.close      seconds (time the full view was open)
//   listing.favorite   on (bool: saved or removed), source (card | popup | gallery), type, price (bucket), kommun, regso
//   listing.share      method (copy | native), type, price (bucket), kommun, regso — the popup's share/copy-link button
//   feed.page          page
//   feed.tab           tab (for_sale | coming | favorites), count (favorites tab: saved homes), view (kartvy: its rail's
//                      tabs; listvy sends none)
//
// Travel time to work (commute.js)
//   commute.place      set (bool) — a workplace was chosen or removed
//   commute.mode       mode (transit | bike | car | walk)
//   commute.limit      minutes (number, or none)
//   commute.route      mode, minutes (bucket: <15 | 15-30 | 30-45 | 45-60 | 60+) — the popup's Pendling section showed a trip
//   commute.trip_map   action (open | mode), from (tile | row; open), mode — the trip drawn on the popup's map
//
// Tools / settings
//   calc.change        field (rate | cash)
//   column.toggle      collapsed (bool) — listvy map column
//   listvy.sheet       sheet (map | filter | restid) — a phone sheet was opened from the chip row under the search
//   help.toggle        collapsed (bool)
//   onboarding.done    view (kartvy | listvy), tip (the last tip's id: a series' last, or a shared tip — commute: its
//                      Lägg in arbetsplats or the focus in the field | switch), step, steps — intro tips (IntroTips.js)
//                      read to the end
//   onboarding.skip    view, tip (the one it was closed on: Hoppa över / Inte nu or Escape; welcome: the welcome before
//                      the series), step, steps
//   faq.view           —        faq.open  question (id of the question that was expanded)
//   theme.toggle       to (light | dark)
//   language.toggle    to (sv | en)
//
// Health
//   error.client       message, source, line
//   perf.listings      ms, source (cache | network), count, view

import { getListingKey } from '../map/listingDetailsApi.js';

export { track, flushAnalytics, initAnalytics, setAnalyticsContext } from './tracker.js';

const toNumber = (value) => {
    if (value === null || value === undefined || value === '' || value === 'min' || value === 'max') return null;
    const parsed = Number(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
};

export const priceBucket = (value) => {
    const price = toNumber(value);
    if (!price) return 'unknown';
    const mkr = price / 1_000_000;
    if (mkr < 1) return '<1 Mkr';
    if (mkr < 2) return '1-2 Mkr';
    if (mkr < 3) return '2-3 Mkr';
    if (mkr < 4) return '3-4 Mkr';
    if (mkr < 5) return '4-5 Mkr';
    if (mkr < 7) return '5-7 Mkr';
    if (mkr < 10) return '7-10 Mkr';
    return '10+ Mkr';
};

const bucketPriceFilter = (value) => {
    const price = toNumber(value);
    return price == null ? undefined : priceBucket(price);
};

const bucketArea = (value) => {
    const area = toNumber(value);
    if (area == null) return undefined;
    if (area < 40) return '<40 m²';
    if (area < 60) return '40-59 m²';
    if (area < 80) return '60-79 m²';
    if (area < 100) return '80-99 m²';
    if (area < 130) return '100-129 m²';
    return '130+ m²';
};

const bucketFee = (value) => {
    const fee = toNumber(value);
    if (fee == null) return undefined;
    if (fee <= 2000) return '≤2 000 kr';
    if (fee <= 3000) return '≤3 000 kr';
    if (fee <= 4000) return '≤4 000 kr';
    if (fee <= 5000) return '≤5 000 kr';
    return '>5 000 kr';
};

const SCORE_FILTERS = {
    totalValue: 'total',
    apartmentValue: 'sqm_apartment',
    houseValue: 'sqm_house',
    incomeValue: 'income',
    unemploymentValue: 'unemployment',
    ownedValue: 'owned',
    educationValue: 'education',
};

// listingFilters (kartvy and listvy share the shape) -> filter.apply props.
export const summarizeFilters = (filters = {}) => {
    const fields = [];
    const props = {};
    if (filters.houseType && filters.houseType !== 'all') {
        fields.push('type');
        props.type = filters.houseType;
    }
    const numeric = [
        ['priceMin', 'price_min', bucketPriceFilter], ['priceMax', 'price_max', bucketPriceFilter],
        ['roomMin', 'rooms_min', toNumber], ['roomMax', 'rooms_max', toNumber],
        ['sqmMin', 'area_min', bucketArea], ['sqmMax', 'area_max', bucketArea],
        ['feeMax', 'fee_max', bucketFee],
        ['sqmPriceMin', 'sqm_price_min', toNumber], ['sqmPriceMax', 'sqm_price_max', toNumber],
        ['floorMin', 'floor_min', toNumber], ['floorMax', 'floor_max', toNumber],
        ['yearBuiltMin', 'year_built_min', toNumber], ['yearBuiltMax', 'year_built_max', toNumber],
    ];
    numeric.forEach(([key, name, bucket]) => {
        const value = filters[key];
        if (value === undefined || value === 'min' || value === 'max' || value === '' || value === null) return;
        const bucketed = bucket(value);
        if (bucketed === undefined || bucketed === null) return;
        fields.push(name);
        props[name] = bucketed;
    });
    ['balcony', 'elevator'].forEach((key) => {
        if (filters[key] === true) fields.push(key);
    });
    if (filters.newConstruction === 'only' || filters.newConstruction === 'exclude') {
        fields.push('new_construction');
        props.new_construction = filters.newConstruction;
    }
    const scoreMetrics = [];
    Object.entries(SCORE_FILTERS).forEach(([key, name]) => {
        const range = filters[key];
        if (!Array.isArray(range) || (Number(range[0]) <= 1 && Number(range[1]) >= 6)) return;
        scoreMetrics.push(name);
        props[`score_${name}`] = `${range[0]}-${range[1]}`;
    });
    if (scoreMetrics.length) {
        fields.push('area_score');
        props.score_metrics = scoreMetrics;
    }
    return { fields, ...props };
};

export const listingProps = (listing = {}) => ({
    type: listing.type || undefined,
    price: priceBucket(listing.priceValue ?? listing.price),
    kommun: listing.kommun || undefined,
    regso: listing.regsoId || listing.regso_id || listing.regso || undefined,
    // The listing's own key ("bostad/123" / "annons/123"), so opens, favourites and realtor click-throughs can
    // be counted per listing in the admin (not personal data — a public listing identifier).
    listing_id: getListingKey(listing) || undefined,
});
